import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../i18n";

// A discreet, editorial language selector. Switching is instant and preserves the current page.
export const LanguageSelector = ({ variant = "header" }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
    || SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code))
    || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    document.documentElement.lang = current.code;
  }, [current.code]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="language-selector-button"
        aria-label="Select language"
        className="flex items-center gap-1 p-2 rounded-none text-secondary-custom hover:text-primary-custom transition-colors duration-300"
      >
        <Globe className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-[10px] font-sans tracking-[0.2em] uppercase">{current.short}</span>
      </button>

      {open && (
        <div
          data-testid="language-menu"
          className="absolute right-0 mt-2 min-w-[160px] bg-primary-custom border border-subtle-custom shadow-2xl z-[70] animate-fade-in"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => choose(l.code)}
              data-testid={`language-option-${l.code}`}
              className={`w-full flex items-center justify-between px-4 py-3 text-left text-xs font-sans tracking-[0.15em] uppercase transition-colors duration-200 ${
                l.code === current.code
                  ? "text-[var(--accent-bronze)]"
                  : "text-secondary-custom hover:text-primary-custom hover:bg-surface-custom"
              }`}
            >
              <span>{l.label}</span>
              {l.code === current.code && <Check className="w-3.5 h-3.5" strokeWidth={2} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
